/**
 * Retrieval pipeline step that fetches CDF timeseries datapoints for a
 * given set of externalIds and time range, producing a wide-format
 * {@link Dataset} with one `timestamp` column plus one numeric column per
 * series.
 *
 * Semantics:
 *
 * - Takes externalIds directly (no inputDataset extraction — that's a
 *   tool-layer concern; the tool adapter can pluck refs off a stored
 *   dataset before invoking this step).
 * - Batches requests into `CDF_BATCH_SIZE` chunks and routes through
 *   `queryClient.fetchQuery` so repeat calls hit the TanStack Query
 *   cache rather than the CDF API.
 * - Automatically computes granularity from the time range when not
 *   explicitly provided.
 * - Produces a wide-format Dataset: one row per unique timestamp with
 *   one number column per series (sparse series are backfilled with
 *   `null`, matching {@link transformToChartFormat}).
 * - `consumesInput` is `false`; the orchestrator passes `undefined` for
 *   input.
 *
 * Requires both `client` and `queryClient` on the {@link PipelineContext}
 * and throws a {@link ClientNotAvailableError} with a clear suggestion if
 * either is missing.
 */
import type { Aggregate, DatapointAggregates, Datapoints } from '@cognite/sdk';
import z from 'zod';

import { CDF_DATAPOINTS_BATCH_SIZE } from '#core/lib/timeseries/cdf-constants.ts';
import { fetchDatapointsBatched } from '#core/lib/timeseries/fetch-datapoints-batched.ts';
import { calculateGranularity } from '#core/lib/timeseries/granularityCalculator.ts';

import { createDataset } from '../dataset.ts';
import type { PipelineStep } from '../step/types.ts';
import type { ColumnSchema } from '../types.ts';
import { AGGREGATES } from '#plugin-chat/lib/timeseries/aggregates.ts';
import { extractDatapointValue } from '#plugin-chat/lib/timeseries/datapoint-value.ts';
import { InvalidSpecError } from '#plugin-chat/lib/tool/errors/index.ts';

/**
 * Column name used for the time axis in datasets produced by `fetchTimeseriesStep`.
 * Stable across the chart-format transform, column schema, downstream adapters,
 * and the `getDatapoints` tool message. Matches the default xAxis
 * of `ChartSchema` in core, so LLMs that omit `xAxis` get correct rendering
 * out of the box.
 */
export const TIMESERIES_TIMESTAMP_COLUMN = 'timestamp';

/** Chart-compatible datapoint format: one row per timestamp with one column per series externalId. */
interface ChartDatapoint {
  timestamp: string;
  [seriesId: string]: string | number | null;
}

/**
 * Transforms CDF datapoints response to chart-compatible format.
 * Groups by timestamp across all series, returning an array of
 * { timestamp, [externalId]: value }. Sparse series are backfilled with null
 * so every row has every column.
 */
function transformToChartFormat(items: (DatapointAggregates | Datapoints)[], aggregate: Aggregate): ChartDatapoint[] {
  const byTimestamp = new Map<number, Record<string, number | null>>();
  const allSeriesIds = new Set<string>();

  for (const item of items) {
    const seriesId = item.externalId ?? String(item.id ?? 'unknown');
    if (item.isString) {
      console.warn(`Skipping string datapoints for series ${seriesId} in chart transformation`);
      continue;
    }
    allSeriesIds.add(seriesId);
    for (const dp of item.datapoints) {
      const timestamp = dp.timestamp.getTime();
      const existing = byTimestamp.get(timestamp) ?? {};
      const value = extractDatapointValue(dp, aggregate);
      if (value !== null) {
        existing[seriesId] = value;
      }
      byTimestamp.set(timestamp, existing);
    }
  }

  return Array.from(byTimestamp.entries())
    .sort((a, b) => a[0] - b[0])
    .map(([ts, values]) => {
      const row: Record<string, string | number | null> = {
        [TIMESERIES_TIMESTAMP_COLUMN]: new Date(ts).toISOString(),
      };
      for (const id of allSeriesIds) {
        row[id] = values[id] ?? null;
      }
      return row as ChartDatapoint;
    });
}

const STEP_ID = 'fetchTimeseries';

const fetchTimeseriesParamsSchema = z.object({
  externalIds: z
    .array(z.string())
    .min(1)
    .describe('Timeseries external IDs to fetch datapoints for. Must be non-empty.'),
  startTime: z.string().describe('ISO 8601 datetime for the start of the time range (e.g. "2024-01-01T00:00:00Z").'),
  endTime: z.string().describe('ISO 8601 datetime for the end of the time range (e.g. "2024-01-07T23:59:59Z").'),
  aggregate: z
    .enum(AGGREGATES)
    .optional()
    .describe('Aggregation applied to each time bucket. Omit for raw datapoints.'),
  granularity: z
    .enum(['1h', '1d', '7d', '30d'])
    .optional()
    .describe('Aggregation bucket width. Auto-computed from the time range when omitted.'),
  title: z.string().optional().describe('Optional human-readable title attached to the produced Dataset.meta.'),
});

type FetchTimeseriesParams = z.infer<typeof fetchTimeseriesParamsSchema>;

function parseTimeRange(startTime: string, endTime: string): { start: Date; end: Date } {
  const start = new Date(startTime);
  const end = new Date(endTime);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    throw new InvalidSpecError(`Invalid datetime format: startTime="${startTime}", endTime="${endTime}".`, {
      suggestions: [
        'Use ISO 8601 format (e.g. "2024-01-01T00:00:00Z").',
        'Ensure both startTime and endTime parse as valid dates.',
      ],
    });
  }
  return { start, end };
}

function buildColumns(rows: readonly Record<string, unknown>[]): ColumnSchema[] {
  const seriesNames = new Set<string>();
  for (const row of rows) {
    for (const key of Object.keys(row)) {
      if (key !== TIMESERIES_TIMESTAMP_COLUMN) {
        seriesNames.add(key);
      }
    }
  }
  return [
    { name: TIMESERIES_TIMESTAMP_COLUMN, type: 'datetime' },
    ...[...seriesNames].map((name): ColumnSchema => ({ name, type: 'number' })),
  ];
}

export const fetchTimeseriesStep: PipelineStep<FetchTimeseriesParams> = {
  id: STEP_ID,
  description:
    'Fetch CDF timeseries datapoints for the given externalIds and time range. Produces a wide-format Dataset with one `timestamp` column plus one numeric column per series.',
  paramsSchema: fetchTimeseriesParamsSchema,
  consumesInput: false,
  execute: async (params, _input, ctx) => {
    const { client, queryClient } = ctx;

    const { externalIds, startTime, endTime, aggregate, title } = params;
    const { start, end } = parseTimeRange(startTime, endTime);
    const granularity = params.granularity ?? calculateGranularity(start.getTime(), end.getTime());

    // Per-series CDF point-budget. The SDK applies `limit` per series, so we
    // size it against the per-batch series count (capped at the SDK's max
    // batch size) — preserving the budget split used before this site
    // delegated batching to {@link fetchDatapointsBatched}.
    const isAggregated = aggregate && granularity;
    const aggregateParams = isAggregated ? { granularity, aggregates: [aggregate] satisfies Aggregate[] } : {};
    const maxDatapoints = isAggregated ? 10_000 : 100_000;
    const seriesPerBatch = Math.min(externalIds.length, CDF_DATAPOINTS_BATCH_SIZE);
    const limit = Math.floor(maxDatapoints / Math.max(seriesPerBatch, 1));

    // Inherit `fetchDatapointsBatched`'s default staleTime so repeated tool
    // invocations within a tool loop hit the per-batch cache instead of
    // refetching identical `(items, spec)` from CDF. The producer-tool layer
    // already dedupes by `(threadId, toolName, args)`, but its key includes
    // `outputDataset`, so it misses when the LLM varies the dataset name
    // between otherwise-identical calls — leaving this layer to absorb the
    // redundancy.
    const seriesByExternalId = await fetchDatapointsBatched({
      client,
      queryClient,
      items: externalIds.map((externalId) => ({ externalId })),
      spec: { start, end, ...aggregateParams, limit },
    });

    const items = [...seriesByExternalId.values()];
    const chartRows = transformToChartFormat(items, aggregate ?? 'average');

    // A query that returned zero datapoints is a valid empty result, not an
    // error — downstream operator steps flow empty datasets through
    // correctly. Surface the signal as a non-fatal warning so the LLM knows
    // why rows are missing and can reason about the query rather than
    // retrying blindly.
    const warnings =
      chartRows.length === 0
        ? [
            `fetchTimeseries returned 0 datapoints for the requested series and time range (${startTime} — ${endTime}). Verify the timeseries external IDs exist in CDF, try expanding the time range, or check if the series has data in the specified period.`,
          ]
        : [];

    return createDataset({
      rows: chartRows,
      columns: buildColumns(chartRows),
      meta: {
        ...buildStepMeta(STEP_ID, title),
        ...(warnings.length > 0 ? { warnings } : {}),
      },
    });
  },
};
